import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './ui';

/**
 * Photo Mode.
 *
 * Entirely client-side, and deliberately so: a photograph of your own Grove is
 * not game state, nothing about it needs the server's opinion, and a screenshot
 * endpoint would be an image-upload surface added for a feature that does not
 * need one. Nothing here is sent anywhere. "Save" writes a PNG to the player's
 * own machine and that is the end of it.
 *
 * What it does is get out of the way: it hides the chrome, lets the scene be
 * zoomed and dragged around, and offers the two cosmetic toggles a Grove
 * already understands — time of day and weather. Both are *view* settings and
 * neither is saved, because a photograph should not silently change how your
 * Grove looks tomorrow.
 *
 * The capture works by drawing the scene's own DOM into an SVG `foreignObject`
 * and rasterising that through a canvas. That keeps it dependency-free — no
 * html2canvas, no bundle cost on every page for a feature used occasionally —
 * at the price of one real limitation, which the UI states plainly rather than
 * hiding: images the browser will not let a canvas touch are dropped, so
 * sprites are inlined first and anything else may not appear.
 */

export type PhotoTime = 'day' | 'dusk' | 'night';
export type PhotoWeather = 'clear' | 'rain' | 'snow' | 'petals' | 'fog';

export interface PhotoSettings {
  time: PhotoTime;
  weather: PhotoWeather;
  zoom: number;
  panX: number;
  panY: number;
}

export const DEFAULT_PHOTO: PhotoSettings = {
  time: 'day',
  weather: 'clear',
  zoom: 1,
  panX: 0,
  panY: 0,
};

/**
 * The control bar. Rendered *outside* the captured element by the caller, which
 * is what makes "hide UI" free rather than something the capture has to filter.
 */
export function PhotoModeBar({
  settings,
  onChange,
  onExit,
  onCapture,
  availableWeather,
  busy,
}: {
  settings: PhotoSettings;
  onChange: (next: PhotoSettings) => void;
  onExit: () => void;
  onCapture: () => void;
  /** Only themes the player owns. Passed in rather than assumed. */
  availableWeather: PhotoWeather[];
  busy?: boolean;
}) {
  const set = <K extends keyof PhotoSettings>(key: K, value: PhotoSettings[K]) =>
    onChange({ ...settings, [key]: value });

  return (
    <div className="photobar pxframe pxframe--raised">
      <div className="photobar__group">
        <span className="tiny mono muted">TIME</span>
        {(['day', 'dusk', 'night'] as PhotoTime[]).map((time) => (
          <button
            key={time}
            type="button"
            className={`chip ${settings.time === time ? 'chip--on' : ''}`}
            onClick={() => set('time', time)}
          >
            {time}
          </button>
        ))}
      </div>

      <div className="photobar__group">
        <span className="tiny mono muted">WEATHER</span>
        {availableWeather.map((weather) => (
          <button
            key={weather}
            type="button"
            className={`chip ${settings.weather === weather ? 'chip--on' : ''}`}
            onClick={() => set('weather', weather)}
          >
            {weather}
          </button>
        ))}
      </div>

      <div className="photobar__group">
        <span className="tiny mono muted">ZOOM</span>
        <input
          type="range"
          min={0.6}
          max={2.4}
          step={0.05}
          value={settings.zoom}
          onChange={(event) => set('zoom', Number(event.target.value))}
          aria-label="Zoom"
        />
        <button
          type="button"
          className="linkbtn tiny"
          onClick={() => onChange({ ...settings, zoom: 1, panX: 0, panY: 0 })}
        >
          Reset
        </button>
      </div>

      <div className="photobar__group photobar__group--end">
        <Button size="sm" variant="gold" onClick={onCapture} loading={busy}>
          Save photo
        </Button>
        <Button size="sm" variant="ghost" onClick={onExit}>
          Exit
        </Button>
      </div>
    </div>
  );
}

/**
 * Wraps the thing being photographed: applies the view transform, the tint and
 * the weather overlay, and handles drag-to-pan.
 *
 * Pan is written straight to a ref'd element's style rather than through React
 * state on every pointer move — the same reasoning `groveLife.ts` uses. A drag
 * that re-renders a scene of thirty-six sprites per frame is a drag that
 * stutters.
 */
export function PhotoFrame({
  settings,
  onChange,
  innerRef,
  children,
}: {
  settings: PhotoSettings;
  onChange: (next: PhotoSettings) => void;
  // `MutableRefObject`, not `RefObject`: this ref starts null and is filled by
  // React, which is exactly what `useRef<T | null>(null)` produces — and what
  // the older `RefObject<T>` signature will not accept.
  innerRef: React.MutableRefObject<HTMLDivElement | null>;
  children: React.ReactNode;
}) {
  const dragging = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const stage = useRef<HTMLDivElement | null>(null);

  const apply = useCallback((zoom: number, panX: number, panY: number) => {
    if (stage.current) {
      stage.current.style.transform = `translate(${panX}px, ${panY}px) scale(${zoom})`;
    }
  }, []);

  useEffect(() => {
    apply(settings.zoom, settings.panX, settings.panY);
  }, [apply, settings.zoom, settings.panX, settings.panY]);

  return (
    <div
      ref={innerRef}
      className={`photoframe photoframe--${settings.time} photoframe--${settings.weather}`}
      onPointerDown={(event) => {
        if (event.pointerType !== 'mouse' && event.pointerType !== 'touch') return;
        dragging.current = {
          x: event.clientX,
          y: event.clientY,
          panX: settings.panX,
          panY: settings.panY,
        };
        (event.target as Element).setPointerCapture?.(event.pointerId);
      }}
      onPointerMove={(event) => {
        const drag = dragging.current;
        if (!drag) return;
        apply(settings.zoom, drag.panX + (event.clientX - drag.x), drag.panY + (event.clientY - drag.y));
      }}
      onPointerUp={(event) => {
        const drag = dragging.current;
        dragging.current = null;
        if (!drag) return;
        // State is written once, on release: the live drag already moved the
        // pixels, so this is only so a re-render does not snap it back.
        onChange({
          ...settings,
          panX: drag.panX + (event.clientX - drag.x),
          panY: drag.panY + (event.clientY - drag.y),
        });
      }}
    >
      <div ref={stage} className="photoframe__stage">
        {children}
      </div>
      <div className="photoframe__weather" aria-hidden />
      <div className="photoframe__vignette" aria-hidden />
    </div>
  );
}

/**
 * Rasterises an element to a PNG and hands it to the browser's download.
 *
 * Sprites are inlined as data URIs first. A canvas cannot read pixels from an
 * image it considers cross-origin, and in development the sprites are served
 * from the API on a different port — so without this step a photograph is a
 * blank Grove, which looks like a bug and is not one.
 */
export async function capturePhoto(element: HTMLElement, filename: string): Promise<void> {
  const rect = element.getBoundingClientRect();
  const scale = Math.min(2, window.devicePixelRatio || 1);

  const clone = element.cloneNode(true) as HTMLElement;
  clone.style.transform = 'none';

  await inlineImages(clone);

  // The scene's own stylesheet has to travel with it — `foreignObject` renders
  // markup with no access to the document's CSS.
  const styles = collectStyles();

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${rect.width}" height="${rect.height}">
    <foreignObject width="100%" height="100%">
      <div xmlns="http://www.w3.org/1999/xhtml" style="width:${rect.width}px;height:${rect.height}px">
        <style>${styles}</style>
        ${new XMLSerializer().serializeToString(clone)}
      </div>
    </foreignObject>
  </svg>`;

  const image = new Image();
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = () => reject(new Error('Could not render the photo.'));
  });

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(rect.width * scale);
  canvas.height = Math.round(rect.height * scale);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not render the photo.');
  // Pokémon sprites are pixel art; smoothing them on the way out would undo the
  // one rendering rule this whole design system insists on.
  context.imageSmoothingEnabled = false;
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Could not save the photo.');

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

async function inlineImages(root: HTMLElement) {
  const images = [...root.querySelectorAll('img')];
  await Promise.all(
    images.map(async (image) => {
      if (image.src.startsWith('data:')) return;
      try {
        const response = await fetch(image.src, { credentials: 'same-origin' });
        const blob = await response.blob();
        image.src = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.readAsDataURL(blob);
        });
      } catch {
        // A sprite that will not load is dropped rather than failing the whole
        // photograph — a Grove missing one Pokémon still beats an error toast.
        image.remove();
      }
    }),
  );
}

/** Every same-origin rule in the document, concatenated. */
function collectStyles(): string {
  let css = '';
  for (const sheet of [...document.styleSheets]) {
    try {
      for (const rule of [...(sheet.cssRules ?? [])]) css += rule.cssText;
    } catch {
      // A stylesheet from another origin throws on `cssRules`. There are none in
      // this app — fonts are self-hosted — but the guard keeps a future CDN
      // stylesheet from breaking Photo Mode silently.
    }
  }
  return css;
}

/** Convenience hook: photo mode on/off plus its settings, in one place. */
export function usePhotoMode() {
  const [active, setActive] = useState(false);
  const [settings, setSettings] = useState<PhotoSettings>(DEFAULT_PHOTO);
  const [busy, setBusy] = useState(false);
  const frame = useRef<HTMLDivElement | null>(null);

  // Escape leaves photo mode. It hides the entire interface, so there has to be
  // a way out that does not depend on finding a button that is also hidden.
  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActive(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('photo-mode');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('photo-mode');
    };
  }, [active]);

  const capture = useCallback(async (filename: string) => {
    if (!frame.current) return;
    setBusy(true);
    try {
      await capturePhoto(frame.current, filename);
    } finally {
      setBusy(false);
    }
  }, []);

  return { active, setActive, settings, setSettings, frame, busy, capture };
}
